/*
 * Copyright 2025 Intuition Robotics ltd
 */
import __asset_0 from "@res/icons/btn_toggle_off_outline.svg";
import __asset_1 from "@res/icons/btn_toggle_on_outline.svg";
import * as React from "react";
import * as emotion from "emotion";
import {_keys, compare, generateHex} from "@intuitionrobotics/ts-common";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/app-frontend/core/BaseComponent";
import {Spacing} from "@styles/styles";
import {FONTS} from "@res/fonts";
import {COLORS} from "@res/colors";
import {ICONS} from "@res/icons";
import {
    CareFieldDef,
    CareFieldVal,
    checkinElements,
    DefaultEveryDay,
    DefaultEveryMonth,
    DefaultEveryWeek,
    DefaultTriceDay,
    DefaultTwiceAWeek,
    DefaultTwiceDay,
    Frequency,
    getRangeByTimeUnitForKey,
    Key_After_Breakfast,
    Key_After_Dinner,
    Key_After_Lunch,
    Key_Before_Breakfast,
    Key_Before_Dinner,
    Key_Before_Lunch,
    SubKeys,
    wellnessCoachElements
} from "@app/app-shared/care_config";
import {ConfigRangeDialog} from "../../ui/patients/ConfigRangeDialog";
import {HGap} from "../../widget/HGap";
import {
    _BaseNodeRenderer,
    Adapter,
    AdapterBuilder,
    ItemToRender,
    MenuModule,
    NodeRendererProps
} from "@intuitionrobotics/thunderstorm/frontend";
import {MenuAndButton} from "@intuitionrobotics/thunderstorm/app-frontend/modules/menu/MenuAndButton";
import {headerStyle} from "../../ui/alerts/AlertRow";
import {hoverStyle} from "../../ui/alerts/StatusEntry";
import {Badge} from "../../widget/Badge";
import {hideTooltip, showTooltip} from "../../widget/appTooltip";
import {IconInfo} from "../../widget/IconInfo";
import dayjs from "../../../utils/dayjs";
import {capitalize} from "@app/ir-q-app-common/types/care-center/user";
import {FrequencySelector} from "../../ui/FrequencySelector";

type State = {
    expanded: boolean
};

type Props = {
    definition: CareFieldDef;
    value: CareFieldVal;
    toggle?: boolean;
    onEnableChange: (v: boolean) => void
    onFrequencyChange: (v: CareFieldVal) => void
    showActions: boolean
};

const off = __asset_0;
const on = __asset_1;

const wrapper = emotion.css(
    {
        boxSizing: "border-box",
        paddingTop: 16,
        paddingBottom: 16
    }
);

const content = emotion.css(
    {
        flex: 1
    }
);

const toggle = emotion.css(
    {
        marginRight: Spacing.m
    }
);

type FrequencyMenuRm = {
    frequency: _BaseNodeRenderer<FrequencyOption>
}

type FrequencyOption = {
    value: string,
    onClick: () => void
}

export class FrequencyEntry
    extends BaseComponent<NodeRendererProps<FrequencyOption>, {}> {

    constructor(props: NodeRendererProps<FrequencyOption>) {
        super(props);
    }

    render() {
        return <div className={hoverStyle} style={{display: "flex", padding: "10px"}}
                    onClick={() => this.props.item.onClick()}>{FONTS.reg_14(
            this.props.item.value)}</div>;
    }
}

export const MenuKey_AlertStatusChange = "alert_status_change";

export class ConfigMeasurementsItem<T extends Frequency>
    extends BaseComponent<Props, State> {

    constructor(props: Props) {
        super(props);
        this.state = {
            expanded: false
        };
    }

    render() {
        return <div id={`config-measurement-item-wrapper-${generateHex((8))}`}
                    className={`ll_h_c match_width ${wrapper}`}>
            <div className={`ll_h_t`} style={{flex: 1}}>
                {this.renderToggle()}
                {this.renderContent()}
            </div>
        </div>;
    }

    private renderContent = () => {
        return <div id={`config-measurement-item-content-${generateHex(8)}`}
                    className={`${content} ll_v_s`}>
            {this.renderUpper()}
            {this.renderFrequency()}
        </div>;
    };

    private configMeasurementTooltip = () => {
        const tooltipDescription = this.props.definition.shortDescription;
        if (tooltipDescription !== undefined) {
            return <div className="ll_v_l">
                {tooltipDescription.map(desc => FONTS.reg_14_secondary_dark(desc))}
            </div>;
        }
    };

    private renderUpper = () => {
        return <div className={`ll_h_t`} style={{justifyContent: "space-between"}}>
            <Badge show={!!this.props.definition.shortDescription} top={6} right={-3} badge={
                <IconInfo
                    onMouseOver={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        showTooltip(
                            e, this.configMeasurementTooltip(),
                            rect.left,
                            rect.top,
                            {
                                backgroundColor: COLORS.white,
                                padding: 8,
                                transform: "translate(5%, -105%)",
                                border: `1px solid ${COLORS.primary_light_blue}`
                            });
                    }}
                    onMouseLeave={(_e) => {
                        hideTooltip();
                        return;
                    }}/>
            }>
                <div style={{marginBottom: 5}}>
                    {
                        this.props.value.enabled ?
                            FONTS.reg_16_dark(this.props.definition.presentation) :
                            FONTS.reg_lightgray_16_L(this.props.definition.presentation)
                    }
                </div>
            </Badge>
            <div style={{justifyContent: "flex-end"}}>
                {
                    this.props.showActions && this.props.value.enabled ? this.props.value.ranges ? this.renderRange() : this.renderSetRange() : ""
                }
                {/*{this.renderExpand()}*/}
            </div>
        </div>;
    };

    private rendererMap: FrequencyMenuRm = {
        frequency: FrequencyEntry
    };


    resolveFrequency = (choice: string): Frequency => {
        switch (choice) {
            case "Daily":
                return {type: this.props.value.frequency.type, times: 1, value: "day"};
            case "Weekly":
                return {type: this.props.value.frequency.type, times: 1, value: "week"};
            case "Monthly":
                return {type: this.props.value.frequency.type, times: 1, value: "month"};
            case "Twice a week":
                return {type: this.props.value.frequency.type, times: 2, value: "week"};
            case "Twice a month":
                return {type: this.props.value.frequency.type, times: 2, value: "month"};
            case "Twice a day":
                return {type: this.props.value.frequency.type, times: 2, value: "day"};
            case "3 times a day":
                return {type: this.props.value.frequency.type, times: 3, value: "day"};
            default:
                return {type: this.props.value.frequency.type, times: 2, value: "week"};
        }
    };


    private resolveFrequencyInverse = (frequency: Frequency): string => {
        if (compare(frequency, DefaultEveryDay))
            return "Daily";
        if (compare(frequency, DefaultEveryWeek))
            return "Weekly";
        if (compare(frequency, DefaultEveryMonth))
            return "Monthly";
        if (compare(frequency, DefaultTwiceAWeek))
            return "Twice a week";
        if (compare(frequency, DefaultTwiceDay))
            return "Twice a day";
        if (compare(frequency, DefaultTriceDay))
            return "3 times a day";
        if (compare(frequency, {type: this.props.value.frequency.type, times: 2, value: "month"}))
            return "Twice a month";

        return "Unknown";
    };

    buildElements = (time: string[]): ItemToRender<FrequencyMenuRm>[] => {
        return time.map(_time => {
            return {
                item: {
                    value: _time,
                    onClick: () => {
                        MenuModule.hide(MenuKey_AlertStatusChange);
                        if (_time === "Custom")
                            return ConfigRangeDialog.show(this.props.definition, this.props.definition.presentation, this.props.value, this.props.onFrequencyChange);

                        this.props.value.frequency = this.resolveFrequency(_time);
                        this.props.onFrequencyChange(this.props.value);
                    }
                },
                type: "frequency"
            };
        });
    };

    private dropdownIcon = () => <div
        style={{backgroundColor: COLORS.primary_blue_10, width: "15px", height: "15px", borderRadius: 20}}>{ICONS.icon_arrow_down(
        COLORS.primary_dark_blue, 16)}</div>;

    private renderFrequencyHeader = (label: string) => {
        return <div className={headerStyle}>
            {FONTS.reg_14(label, {paddingTop: "1px", color: COLORS.primary_blue})}
            {<div style={{paddingLeft: "8px", paddingTop: "1px"}}>{this.dropdownIcon()}</div>}
        </div>;
    };

    private renderFrequency = () => {
        const enabled = this.props.value.enabled;
        if (!enabled) return null;

        const _frequency = this.props.value.frequency;

        if (wellnessCoachElements.includes(this.props.definition.key)) {
            return <FrequencySelector
                label={(_frequency.times === 1) ? "session" : "sessions"}
                frequency={_frequency}
                range={getRangeByTimeUnitForKey(_frequency.value, this.props.definition.key)}
                timeUnits={this.props.definition.timeUnits ? this.props.definition.timeUnits : ["day", "week"]}
                onFrequencyChange={(frequency) => {
                    this.props.onFrequencyChange({...this.props.value, frequency});
                }}/>;
        }

        let label = `${_frequency.times} ${_frequency.times === 1 ? "time" : "times"} a ${_frequency.value}`;
        if (this.props.value.ttl) {
            const how_many_days = dayjs(this.props.value.ttl).diff(dayjs(), "day");
            if (how_many_days > 0)
                label += `, ends in ${how_many_days} days`;
        }
        const timeArray = checkinElements.includes(this.props.definition.key) ? [
            "Daily",
            "Weekly",
            "Monthly"
        ] : this.props.definition.options.map(o => this.resolveFrequencyInverse(o)).concat(["Custom"]);

        const adapter: Adapter = AdapterBuilder()
            .list()
            .multiRender(this.rendererMap)
            .setData(this.buildElements(timeArray))
            .build();

        const componentText = this.renderFrequencyHeader(label);
        return <MenuAndButton
            id={MenuKey_AlertStatusChange}
            iconOpen={componentText}
            iconClosed={componentText}
            adapter={adapter}/>;

    };

    private renderSetRange = () => {
        return this.props.value.enabled &&
            <div className={"clickable"}
                 style={{backgroundColor: COLORS.primary_blue, padding: "8px", borderRadius: 4, display: "flex", alignItems: "center"}}
                 onClick={() => ConfigRangeDialog.show(this.props.definition, this.props.definition.presentation, this.props.value,
                     this.props.onFrequencyChange)}>
                {ICONS.health_shield_white("white", 14)}
                {FONTS.semi_bold_14("Set healthy range", {color: COLORS.white, marginLeft: "5px"})}
            </div>;
    };

    private renderRange = () => {
        const ranges = this.props.value.ranges;
        const subkeys = this.props.definition.subKeys;
        if (!ranges || !subkeys || !Object.keys(ranges).length)
            return this.renderSetRange();

        let rangeString = "";
        _keys<SubKeys, string>(subkeys).filter(_key => ![Key_Before_Dinner,
            Key_After_Breakfast,
            Key_After_Dinner].includes(_key)).forEach((_key, i) => {
            const minAndMax = ranges[_key];
            if (!minAndMax) return;

            let keyToShow = _key;
            if (keyToShow === Key_Before_Breakfast)
                keyToShow = "fasted";
            else if ([Key_Before_Lunch].includes(keyToShow))
                keyToShow = "before_meal";
            else if ([Key_After_Lunch].includes(keyToShow))
                keyToShow = "after_meal";
            rangeString = rangeString + `${i !== 0 ? ", " : ""}${minAndMax.min}-${minAndMax.max} ${capitalize(keyToShow).replace("_",
                " ")/* _key.substr(0, 4).toUpperCase()*/}`;
        });
        return <div style={{display: "flex", alignItems: "center"}}
                    onClick={() => ConfigRangeDialog.show(this.props.definition, this.props.definition.presentation, this.props.value,
                        this.props.onFrequencyChange, true)}>
            {ICONS.health_shield_blue("rgb(83, 186, 213)", 14)}
            {FONTS.semi_bold_14("Health Alert:", {marginLeft: "5px"})}
            <HGap h={8}/>
            {FONTS.reg_14(rangeString, {color: COLORS.primary_blue, textDecoration: "underline", cursor: "pointer"})}
        </div>;
    };

    private renderToggle = () => {
        const enabled = this.props.value.enabled;
        return <div id={`config-measurement-item-toggle-${generateHex((8))}`} className={toggle}>
            <div style={{width: 48, height: 24, cursor: this.props.toggle ? "pointer" : "default"}}
                 onClick={() => {
                     if (!this.props.toggle)
                         return;
                     if (enabled)
                         this.setState({expanded: false});
                     this.props.onEnableChange(!enabled);
                 }}>
                {
                    enabled ? <img alt="on" src={on}/> : <img alt="off" src={off}/>
                }
            </div>
        </div>;
    };

    renderExpand = () => {
        const enabled = this.props.value.enabled;
        return <div id={`config-measurement-item-expand-icon-${generateHex((8))}`}
                    style={{width: 24, height: 24, cursor: "pointer", alignSelf: "flex-start"}}
                    onClick={() => enabled && this.setState({expanded: !this.state.expanded})}>
            {
                !enabled || !this.state.expanded ?
                    <div style={{
                        width: 24, height: 24,
                        opacity: enabled ? 1 : 0.5
                    }}>
                        {
                            ICONS.icon_arrowhead_xl_down(COLORS.neutral_gray_1)
                        }
                    </div> :
                    <div style={{
                        width: 24, height: 24,
                        opacity: enabled ? 1 : 0.5
                    }}>
                        {
                            ICONS.icon_arrowhead_xl_up(COLORS.neutral_gray_1)
                        }
                    </div>
            }
        </div>;
    };

}
